// Content for /tools/ai-visibility-check/ (EN source strings; ms/zh come from the
// key-as-English dictionaries via t()).
//
// What the check IS comes only from what the site already promises (AI SEO page FAQ
// + enquiry section, SeoEnquiryForm): five customer questions put to ChatGPT, Gemini
// and Google AI Overviews; who gets named instead; which sources are cited; what is
// holding the website back; then a recommended plan. Free, no obligation. No
// turnaround is stated on this page, and none may be added without a source.
//
// The sample report is ILLUSTRATIVE. The business and every competitor are fictional
// placeholders ("Clinic A/B/C") so no real business is ever named.
//
// Facts in WHY_IT_MATTERS are reused from blog/get-recommended-by-chatgpt-google-ai-overviews
// (research: docs/research/ai-answer-recommendation-how-to.md). Do not add stats that
// are not cited there.

export const AIVC_ROUTE = "tools/ai-visibility-check";
export const AIVC_PATH = "/tools/ai-visibility-check/";
export const AIVC_FORM_ID = "ai-visibility-form";
export const SINGAPORE_AI_SEO_PATH = "/singapore/ai-seo/";

export type AiAssistant = "ChatGPT" | "Gemini" | "Google AI Overviews";
export const ASSISTANTS: readonly AiAssistant[] = [
  "ChatGPT",
  "Gemini",
  "Google AI Overviews",
] as const;

/** "Your clinic" is the business being checked; everything else is a placeholder. */
export const YOUR_BUSINESS = "Your clinic";

/** One answer cell: who was named, or null when no AI answer was shown. */
export type SampleAnswer = { named: string[] } | null;

export interface SampleQuestion {
  question: string;
  answers: Record<AiAssistant, SampleAnswer>;
}

export const SAMPLE_REPORT = {
  label: "Illustrative example",
  heading: "Example: a dental clinic in Petaling Jaya",
  disclaimer:
    "This is a made-up example to show the format. The clinic, Clinic A, Clinic B and Clinic C are fictional, and the results are not real data.",
  questions: [
    {
      question: "Which dental clinic in Petaling Jaya is good for braces?",
      answers: {
        ChatGPT: { named: ["Clinic A", "Clinic B"] },
        Gemini: { named: ["Clinic A"] },
        "Google AI Overviews": { named: ["Clinic B", "Clinic C"] },
      },
    },
    {
      question: "Where can I get my teeth cleaned in PJ on a Saturday?",
      answers: {
        ChatGPT: { named: ["Clinic C"] },
        Gemini: { named: ["Clinic A", "Clinic C"] },
        "Google AI Overviews": { named: [YOUR_BUSINESS, "Clinic A"] },
      },
    },
    {
      question: "How much does a dental implant cost in Petaling Jaya?",
      answers: {
        ChatGPT: { named: [] },
        Gemini: { named: ["Clinic B"] },
        "Google AI Overviews": { named: ["Clinic B"] },
      },
    },
    {
      question: "Which dentist in PJ is good with children?",
      answers: {
        ChatGPT: { named: ["Clinic A"] },
        Gemini: { named: [YOUR_BUSINESS] },
        "Google AI Overviews": { named: ["Clinic A"] },
      },
    },
    {
      question: "Is there an emergency dentist open late in Petaling Jaya?",
      answers: {
        ChatGPT: { named: ["Clinic C"] },
        Gemini: { named: ["Clinic C"] },
        "Google AI Overviews": null,
      },
    },
  ] satisfies SampleQuestion[],
  sources: [
    { source: "A “best dentists in PJ” list article", cited: 5 },
    { source: "Google Business Profile listings", cited: 4 },
    { source: "Clinic B's treatment price page", cited: 3 },
    { source: "A parenting forum thread", cited: 2 },
    { source: "Your clinic's website", cited: 1 },
  ],
  fixes: [
    {
      title: "Say where you are in plain words",
      body: "Your service pages never mention Petaling Jaya, so answers to PJ questions pass you by.",
    },
    {
      title: "Let ChatGPT's search crawler in",
      body: "Your robots.txt blocks OAI-SearchBot, and OpenAI says blocked sites are not shown in ChatGPT search answers.",
    },
    {
      title: "Answer the price question",
      body: "Clinic B publishes implant prices, and both answers to the price question cite that page. Your site gives no price guide.",
    },
    {
      title: "Use one name everywhere",
      body: "Your clinic's name on the website does not match your Google Business Profile.",
    },
  ],
  recommendation:
    "Recommended next step in this example: AI SEO Essential, to fix the crawler block, location wording and listings first.",
};

/** Count how often the checked business was named, and out of how many answers shown. */
export function sampleReportScore(questions: readonly SampleQuestion[] = SAMPLE_REPORT.questions) {
  let named = 0;
  let shown = 0;
  for (const q of questions) {
    for (const assistant of ASSISTANTS) {
      const answer = q.answers[assistant];
      if (!answer) continue;
      shown += 1;
      if (answer.named.includes(YOUR_BUSINESS)) named += 1;
    }
  }
  return { named, shown, total: questions.length * ASSISTANTS.length };
}

export const STEPS = [
  {
    title: "Tell us about your business",
    body: "Fill in the form with your website and what you sell. Tick the problems you see, if any.",
  },
  {
    title: "We ask the AI assistants",
    body: "We put five questions your customers would ask to ChatGPT, Gemini and Google AI Overviews, and record who is named and which sources are cited.",
  },
  {
    title: "You get the findings and a plan",
    body: "We show you who gets named instead, what is holding your website back, and which plan fits: Essential, Growth or Managed. No obligation.",
  },
];

export const WHY_IT_MATTERS = [
  {
    stat: "1st and 59th",
    body: "OpenAI's own country data ranks Singapore first of 147 countries for ChatGPT messages per head in the second quarter of 2026, and Malaysia 59th.",
    sourceLabel: "OpenAI country data",
    sourceUrl: "https://openai.com/signals/data-download/",
  },
  {
    stat: "Malay and Chinese",
    body: "Google lists AI Overviews and AI Mode in both Malaysia and Singapore, with Malay and Chinese among the supported languages.",
    sourceLabel: "Google AI Overviews help",
    sourceUrl: "https://support.google.com/websearch/answer/14901683",
  },
  {
    stat: "No paid shortcut",
    body: "OpenAI says ads “do not influence ChatGPT's answers”, and Google says there is no way to pay for a better local ranking.",
    sourceLabel: "OpenAI ads in ChatGPT",
    sourceUrl: "https://help.openai.com/en/articles/20001047-ads-in-chatgpt",
  },
];

export const FAQS = [
  {
    question: "What is a free AI visibility check?",
    answer:
      "We ask ChatGPT, Gemini and Google AI Overviews five questions your customers would ask. Then we show you who gets named instead, which sources are cited, and what is holding your website back, and recommend a plan. It is free, with no obligation.",
  },
  {
    question: "How do I check if ChatGPT recommends my business?",
    answer:
      "You can start yourself: ask ChatGPT the question your best customer asked last month, in a private window, and note who it names. Our free check does this for five customer questions across ChatGPT, Gemini and Google AI Overviews, and adds the sources each answer cites and what to fix.",
  },
  {
    question: "My business ranks on Google. Is it on ChatGPT too?",
    answer:
      "Not necessarily. Ranking on Google does not guarantee being named in ChatGPT, Gemini or Google AI Overviews. AI answers also lean on how plainly a page says what you do and where, and on what other sites say about you. The check shows you where you stand.",
  },
  {
    question: "What do you need from me?",
    answer:
      "Your name, work email, company and website address. You can also tick the problems you see and tell us what you sell. If you do not have a website yet, choose “I need a new website” in the form.",
  },
  {
    question: "Can you guarantee ChatGPT will recommend my business?",
    answer:
      "No. Nobody can, and you should be wary of anyone who does. OpenAI says placement in ChatGPT search is not guaranteed, and Google says AI Overviews often do not appear at all. The check gives you an honest baseline and a list of what to fix.",
  },
];
